// Builds the image of an empty canvas (a single solid color) to upload when the canvas is created.
//
// Drawing it on a <canvas> and calling toBlob means allocating and encoding width * height * 4 bytes
// (100 MB for 5000x5000), which is slow and produces a big upload. A solid color fits a 1-bit palette PNG
// instead: every pixel is index 0, so the raw data is just zeroed rows (~3 MB at the max size) that deflate
// down to a few KB in a few milliseconds.

import { canEncodeIndexedPng, encodeIndexedPng } from './pngEncoder';

const hexToRgb = (hex: string): [number, number, number] => {
  const match = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);
  if (!match) return [255, 255, 255];
  return [Number.parseInt(match[1], 16), Number.parseInt(match[2], 16), Number.parseInt(match[3], 16)];
};

const encodeSolidPng = (width: number, height: number, color: string): Promise<Blob> =>
  encodeIndexedPng({ width, height, palette: [hexToRgb(color)] });

const drawSolidPng = (width: number, height: number, color: string): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      reject(new Error('No se pudo generar la imagen del canvas'));
      return;
    }
    context.fillStyle = color;
    context.fillRect(0, 0, width, height);
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('No se pudo generar la imagen del canvas'))), 'image/png');
  });

export const createBlankCanvasImage = async (
  width: number,
  height: number,
  color = '#FFFFFF',
  fileName = 'canvas.png'
): Promise<File> => {
  // CompressionStream is in every current browser; the <canvas> path only covers older ones.
  const blob = canEncodeIndexedPng()
    ? await encodeSolidPng(width, height, color)
    : await drawSolidPng(width, height, color);
  return new File([blob], fileName, { type: 'image/png' });
};
